// @ts-check
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { leadsApi } from "@/api/endpoints/leads.js";
import { toast } from "@/shared/ui/toaster.jsx";

/**
 * Hooks de dados do módulo comercial.
 *
 * Ficam separados dos componentes: as telas dizem *o que* querem, não *como*
 * buscar. No app antigo cada `queryFn` montava a query do Supabase à mão e
 * ainda importava dados falsos no meio do caminho quando algo dava errado.
 */

/**
 * @typedef {object} Lead
 * @property {string} id
 * @property {string} name
 * @property {string} phone
 * @property {string} origin
 * @property {string} campaign
 * @property {string} channel
 * @property {string} status
 * @property {string} temperature
 * @property {string} owner
 * @property {string} procedure
 * @property {string} summary
 * @property {string} createdAt
 * @property {string} [updatedAt]
 * @property {string|null} [lastInteractionAt]
 * @property {number} [messageCount]
 */

const chaves = {
  todos: ["leads"],
  lista: (filtros) => ["leads", "list", filtros ?? {}],
  origens: ["leads", "origins"],
  conversas: ["leads", "conversations"],
  agenda: ["leads", "agenda"],
  mensagens: (leadId) => ["leads", leadId, "messages"],
  anotacoes: (leadId) => ["leads", leadId, "notes"],
};

export { chaves as leadKeys };

/** @param {{ search?: string, status?: string, origin?: string }} [filtros] */
export function useLeads(filtros) {
  return useQuery({
    queryKey: chaves.lista(filtros),
    queryFn: async () => (await leadsApi.list(filtros)).leads,
    placeholderData: (anterior) => anterior, // evita piscar ao trocar o filtro
  });
}

export function useLeadOrigins() {
  return useQuery({
    queryKey: chaves.origens,
    queryFn: async () => (await leadsApi.origins()).origins,
    staleTime: 5 * 60 * 1000,
  });
}

export function useConversations() {
  return useQuery({
    queryKey: chaves.conversas,
    queryFn: async () => (await leadsApi.conversations()).conversations,
  });
}

export function useAgendaItems() {
  return useQuery({
    queryKey: chaves.agenda,
    queryFn: async () => (await leadsApi.agenda()).items,
  });
}

export function useCreateLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dados) => (await leadsApi.create(dados)).lead,
    onSuccess: (lead) => {
      qc.invalidateQueries({ queryKey: chaves.todos });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success(`Lead ${lead.name} criado.`);
    },
    onError: (erro) => toast.error(erro.message),
  });
}

export function useUpdateLead() {
  const qc = useQueryClient();
  return useMutation({
    /** @param {{ id: string } & Partial<Lead>} variaveis */
    mutationFn: async ({ id, ...patch }) => (await leadsApi.update(id, patch)).lead,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: chaves.todos });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (erro) => toast.error(erro.message),
  });
}

/**
 * Mudança de etapa no kanban, com atualização otimista: o card se move na
 * hora e volta sozinho se o servidor recusar.
 */
export function useUpdateLeadStatus() {
  const qc = useQueryClient();

  return useMutation({
    /** @param {{ id: string, status: string }} variaveis */
    mutationFn: async ({ id, status }) => (await leadsApi.updateStatus(id, status)).lead,

    /** @param {{ id: string, status: string }} variaveis */
    onMutate: async ({ id, status }) => {
      await qc.cancelQueries({ queryKey: chaves.todos });
      const anteriores = qc.getQueriesData({ queryKey: ["leads", "list"] });

      for (const [chave, dados] of anteriores) {
        if (!Array.isArray(dados)) continue;
        qc.setQueryData(
          chave,
          dados.map((lead) => (lead.id === id ? { ...lead, status } : lead)),
        );
      }

      return { anteriores };
    },

    onError: (erro, _variaveis, contexto) => {
      contexto?.anteriores?.forEach(([chave, dados]) => qc.setQueryData(chave, dados));
      toast.error(erro.message ?? "Não foi possível mover o lead.");
    },

    onSettled: () => {
      qc.invalidateQueries({ queryKey: chaves.todos });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export function useDeleteLead() {
  const qc = useQueryClient();
  return useMutation({
    /** @param {string} id */
    mutationFn: (id) => leadsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: chaves.todos });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success("Lead excluído.");
    },
    onError: (erro) => toast.error(erro.message),
  });
}

// ---------------------------------------------------------------- mensagens

/** @param {string | null} leadId */
export function useLeadMessages(leadId) {
  return useQuery({
    queryKey: chaves.mensagens(leadId),
    queryFn: async () => (await leadsApi.messages(leadId)).messages,
    enabled: Boolean(leadId),
  });
}

export function useSendMessage() {
  const qc = useQueryClient();
  return useMutation({
    /** @param {{ leadId: string, text: string, sender?: string }} variaveis */
    mutationFn: async ({ leadId, text, sender = "agent" }) =>
      (await leadsApi.sendMessage(leadId, { text, sender })).message,
    /** @param {unknown} _dados @param {{ leadId: string }} variaveis */
    onSuccess: (_dados, { leadId }) => {
      qc.invalidateQueries({ queryKey: chaves.mensagens(leadId) });
      qc.invalidateQueries({ queryKey: chaves.conversas });
    },
    onError: (erro) => toast.error(erro.message),
  });
}

// ---------------------------------------------------------------- anotações

/** @param {string | null} leadId */
export function useLeadNotes(leadId) {
  return useQuery({
    queryKey: chaves.anotacoes(leadId),
    queryFn: async () => (await leadsApi.notes(leadId)).notes,
    enabled: Boolean(leadId),
  });
}

export function useAddNote() {
  const qc = useQueryClient();
  return useMutation({
    /** @param {{ leadId: string, type: string, text: string, reminderAt?: string|null }} variaveis */
    mutationFn: async ({ leadId, ...dados }) => (await leadsApi.addNote(leadId, dados)).note,
    /** @param {unknown} _nota @param {{ leadId: string }} variaveis */
    onSuccess: (_nota, { leadId }) => {
      qc.invalidateQueries({ queryKey: chaves.anotacoes(leadId) });
      // Uma anotação de retorno vira um compromisso na Agenda.
      qc.invalidateQueries({ queryKey: chaves.agenda });
      toast.success("Anotação adicionada.");
    },
    onError: (erro) => toast.error(erro.message),
  });
}

export function useDeleteNote() {
  const qc = useQueryClient();
  return useMutation({
    /** @param {{ noteId: string, leadId: string }} variaveis */
    mutationFn: ({ noteId }) => leadsApi.removeNote(noteId),
    /** @param {unknown} _dados @param {{ leadId: string }} variaveis */
    onSuccess: (_dados, { leadId }) => {
      qc.invalidateQueries({ queryKey: chaves.anotacoes(leadId) });
      qc.invalidateQueries({ queryKey: chaves.agenda });
      toast.success("Anotação removida.");
    },
    onError: (erro) => toast.error(erro.message),
  });
}

export function useToggleNoteDone() {
  const qc = useQueryClient();
  return useMutation({
    /** @param {{ noteId: string, done: boolean }} variaveis */
    mutationFn: ({ noteId, done }) => leadsApi.setNoteDone(noteId, done),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: chaves.agenda });
      qc.invalidateQueries({ queryKey: ["leads"] });
    },
    onError: (erro) => toast.error(erro.message),
  });
}
